import React, { useState, useRef, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import { useAISidebar } from "../../contexts/AISidebarContext";
import { useTranscription } from "../../contexts/TranscriptionContext";
import { useDemo } from "../../contexts/DemoContext";
import { Play, Pause, Square, Mic } from "lucide-react";
import {
  SectionCard,
  SectionTitle,
  ContentSection,
  ActionBar,
  ActionButton,
  SummaryTextArea,
} from "./NotesManager.styled";
import styled from "styled-components";

// Main container: no scroll on outer sidebar, tight gaps
const MainSidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  gap: -4px; 
  padding: ${({ theme }) => theme.spacing[0.5]} 0;
  background: ${({ theme }) => theme.isDarkMode ? '#0f172a' : theme.colors.surface.default};
`;

// Smaller section titles
const SmallSectionTitle = styled(SectionTitle)`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  padding: 0;
`;

// Cards with proportional heights and minimal padding
const SectionCardTranscription = styled(SectionCard)`
  flex: 0 0 46%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing[0.5]};
  background: ${({ theme }) => theme.isDarkMode ? '#0f172a' : theme.colors.surface.default};
  border: 1px solid ${({ theme }) => theme.isDarkMode ? '#1e293b' : theme.colors.neutral[200]};
`;

const SectionCardSmartNotes = styled(SectionCard)`
  flex: 0 1 32%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing[0.5]};
  overflow-y: auto;
  background: ${({ theme }) => theme.isDarkMode ? '#0f172a' : theme.colors.surface.default};
  border: 1px solid ${({ theme }) => theme.isDarkMode ? '#1e293b' : theme.colors.neutral[200]};
`;

const SectionCardAI = styled(SectionCard)`
  flex: 0 0 16%;
  margin-top: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing[0.5]};
  background: ${({ theme }) => theme.isDarkMode ? '#0f172a' : theme.colors.surface.default};
  border: 1px solid ${({ theme }) => theme.isDarkMode ? '#1e293b' : theme.colors.neutral[200]};
`;

// Transcript scroll area
const TranscriptScrollContainer = styled(ContentSection)`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  background: ${({ theme }) => theme.isDarkMode ? '#0f172a' : theme.colors.surface.default};
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: ${({ theme }) => theme.isDarkMode ? '#1e293b' : theme.colors.surface.base}; }
  &::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.isDarkMode ? '#334155' : theme.colors.neutral[700]}; border-radius: ${({ theme }) => theme.radii.full}; }
  &::-webkit-scrollbar-thumb:hover { background: ${({ theme }) => theme.isDarkMode ? '#475569' : theme.colors.neutral[600]}; }
`;

// Transcript entries
const TranscriptEntry = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[1]};
  background: ${({ theme }) => theme.isDarkMode ? '#1e293b' : 'transparent'};
  border-radius: ${({ theme }) => theme.radii.md};
`;

const TranscriptMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.isDarkMode ? theme.colors.white : theme.colors.accent.light};
  margin-bottom: ${({ theme }) => theme.spacing[0.5]};
`;

const TranscriptText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};
`;

const TranscriptEmpty = styled.div`
  color: ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[500] : theme.colors.neutral[600]};
  text-align: center;
  padding: ${({ theme }) => theme.spacing[2]};
  font-style: italic;
`;

// Section header row
const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

// Recording controls bar
const ControlsBar = styled(ActionBar)`
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  margin: 0;
  padding: 0;
`;

const ControlButton = styled(ActionButton)`
  background: ${({ $recording, theme }) => $recording ? theme.colors.error.light : theme.colors.success.light};
  color: ${({ $recording, theme }) => $recording ? theme.colors.error.main : theme.colors.success.main};
  padding: ${({ theme }) => theme.spacing[0.5]};
  height: 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  svg { width: 14px; height: 14px; }
`;

// Quick action tags row
const QuickActionTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[0.5]};
  margin-bottom: ${({ theme }) => theme.spacing[0.5]};
`;

const ActionTag = styled(ActionButton)`
  background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.raised : theme.colors.accent.surface};
  color: ${({ theme }) => theme.isDarkMode ? '#fff' : theme.colors.accent.main};
  font-size: 0.70rem;
  padding: ${({ theme }) => theme.spacing[0.5]} ${({ theme }) => theme.spacing[1]};
  border-radius: ${({ theme }) => theme.radii.full};
  &:hover { background: ${({ theme }) => theme.colors.accent.hover}; color: #fff; }
`;

// Quick highlight buttons
const QuickHighlightBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[0.5]};
  margin-bottom: ${({ theme }) => theme.spacing[0.5]};
`;

const QuickHighlightButton = styled(ActionButton)`
  background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.raised : theme.colors.warning.light};
  color: ${({ theme }) => theme.isDarkMode ? '#FFD700' : theme.colors.warning.main};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  padding: ${({ theme }) => theme.spacing[0.5]} ${({ theme }) => theme.spacing[1]};
  border-radius: ${({ theme }) => theme.radii.sm};
  &:hover { background: ${({ theme }) => theme.colors.warning.dark}; color: ${({ theme }) => theme.colors.warning.contrastText || '#fff'}; }
`;

// Smart notes input and button
const SmartNoteInput = styled(SummaryTextArea)`
  flex: 1;
  padding: ${({ theme }) => theme.spacing[1]};
  margin-bottom: ${({ theme }) => theme.spacing[0.5]};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  max-height: 90px;
  overflow-y: auto;
  min-height: unset;
  resize: vertical;
`;

const SaveNoteButton = styled(ActionButton)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[0.5]};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  margin-top: ${({ theme }) => theme.spacing[0.5]};
  justify-content: center;
  background: ${({ theme }) => theme.colors.accent.main};
  color: #fff;
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  &:hover { background: ${({ theme }) => theme.colors.accent.hover}; }
`;

// AI input & send
const AIInputContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[0.5]};
  margin-bottom: ${({ theme }) => theme.spacing[0.5]};
`;

const AIInput = styled.input`
  flex: 1;
  padding: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme }) => theme.colors.surface.raised};
  color: ${({ theme }) => theme.colors.neutral[800]};
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.accent.main}; }
  &::placeholder { color: ${({ theme }) => theme.colors.neutral[500]}; }
`;

const SendButton = styled(ActionButton)`
  padding: ${({ theme }) => theme.spacing[0.5]} ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  background: ${({ theme }) => theme.colors.accent.main};
  color: #fff;
  &:hover { background: ${({ theme }) => theme.colors.accent.hover}; }
`;

// Add a new wrapper for demo mode sections
const DemoModeWrapper = styled.div`
  pointer-events: none;
  user-select: none;
  height: 100%;
`;

// Main component
const TranscriptionDisplay = ({ 
  transcript = [], 
  isRecording, 
  setIsRecording, 
  isPaused, 
  setIsPaused, 
  onHighlightSummary 
}) => {
  const { addHighlight } = useAISidebar();
  const { startRecording, stopRecording } = useTranscription();
  const { isDemoMode } = useDemo();
  const transcriptContainerRef = useRef(null);
  const [smartNoteText, setSmartNoteText] = useState("");
  const [aiInput, setAiInput] = useState("");

  const safeTranscript = Array.isArray(transcript) ? transcript : [];

  useLayoutEffect(() => {
    const c = transcriptContainerRef.current;
    if (c) c.scrollTop = c.scrollHeight;
  }, [safeTranscript]);

  // Controls
  const handleStart = () => startRecording();
  const handlePause = () => setIsPaused(true);
  const handleResume = () => setIsPaused(false);
  const handleStop = () => stopRecording();

  // Highlight logic
  const handleQuickHighlight = (secs) => {
    if (isDemoMode) return; // Prevent highlight during demo mode
    
    const last = safeTranscript.length - 1;
    if (last < 0) return;
    const toSec = (t) => t.split(":").map(Number).reduce((a, b) => a * 60 + b, 0);
    const cutoff = toSec(safeTranscript[last].time) - secs;
    const seg = safeTranscript.filter(e => toSec(e.time) >= cutoff);
    const summary = seg.map(e => `${e.speaker}: ${e.text}`).join(" ");
    setSmartNoteText(`Highlighted: ${summary}`);
  };

  // Save note
  const handleSave = () => {
    if (isDemoMode) return; // Prevent save during demo mode
    
    if (smartNoteText.trim()) {
      onHighlightSummary(smartNoteText);
      setSmartNoteText("");
    }
  };

  // Send AI input
  const handleSend = () => {
    if (isDemoMode) return; // Prevent AI input during demo mode
    
    if (aiInput.trim()) {
      // call AI sidebar context here
      setAiInput("");
    }
  };

  const renderTranscriptSection = () => (
    <SectionCardTranscription>
      <SectionHeader>
        <SmallSectionTitle>Live Transcription</SmallSectionTitle>
        <ControlsBar>
          {!isRecording ? (
            <ControlButton onClick={handleStart}><Mic size={14} /></ControlButton>
          ) : isPaused ? (
            <ControlButton onClick={handleResume}><Play size={14} /></ControlButton>
          ) : (
            <ControlButton onClick={handlePause}><Pause size={14} /></ControlButton>
          )}
          {isRecording && <ControlButton onClick={handleStop} $recording><Square size={14} /></ControlButton>}
        </ControlsBar>
      </SectionHeader>
      <TranscriptScrollContainer ref={transcriptContainerRef}>
        {safeTranscript.length > 0 ? (
          safeTranscript.map((line, idx) => (
            <TranscriptEntry key={idx}>
              <TranscriptMeta>{line.time} – {line.speaker}</TranscriptMeta>
              <TranscriptText>{line.text}</TranscriptText>
            </TranscriptEntry>
          ))
        ) : (
          <TranscriptEmpty>Meeting transcript will appear here once recording starts…</TranscriptEmpty>
        )}
      </TranscriptScrollContainer>
    </SectionCardTranscription>
  );

  const renderSmartNotesSection = () => {
    const content = (
      <SectionCardSmartNotes>
        <SectionHeader>
          <SmallSectionTitle>Smart Notes</SmallSectionTitle>
        </SectionHeader>
        <QuickActionTags>
          <ActionTag><span role="img" aria-label="tag">🏷️</span> Tag</ActionTag>
          <ActionTag><span role="img" aria-label="highlight">✨</span> Highlight</ActionTag>
          <ActionTag><span role="img" aria-label="action">⚡</span> Action</ActionTag>
        </QuickActionTags>
        <QuickHighlightBar>
          <QuickHighlightButton onClick={() => handleQuickHighlight(30)}>★ 30s</QuickHighlightButton>
          <QuickHighlightButton onClick={() => handleQuickHighlight(60)}>★ 1m</QuickHighlightButton>
          <QuickHighlightButton onClick={() => handleQuickHighlight(300)}>★ 5m</QuickHighlightButton>
        </QuickHighlightBar>
        <SmartNoteInput
          value={smartNoteText}
          onChange={e => setSmartNoteText(e.target.value)}
          placeholder="Type your note here..."
        />
        <SaveNoteButton onClick={handleSave}>Save Note</SaveNoteButton>
      </SectionCardSmartNotes>
    );

    return isDemoMode ? (
      <DemoModeWrapper>{content}</DemoModeWrapper>
    ) : content;
  };

  const renderAIAssistantSection = () => {
    const content = (
      <SectionCardAI>
        <SectionHeader>
          <SmallSectionTitle>AI Assistant</SmallSectionTitle>
        </SectionHeader>
        <QuickActionTags>
          <ActionTag>Sales Coach</ActionTag>
          <ActionTag>Open Window</ActionTag>
          <ActionTag>Add Task</ActionTag>
          <ActionTag>Add Follow Up</ActionTag>
          <ActionTag>Research</ActionTag>
          <ActionTag>Search</ActionTag>
        </QuickActionTags>
        <AIInputContainer>
          <AIInput
            value={aiInput}
            onChange={e => setAiInput(e.target.value)}
            placeholder="Type a question or command..."
          />
          <SendButton onClick={handleSend}>Send</SendButton>
        </AIInputContainer>
      </SectionCardAI>
    );

    return isDemoMode ? (
      <DemoModeWrapper>{content}</DemoModeWrapper>
    ) : content;
  };

  return (
    <MainSidebarContent>
      {renderTranscriptSection()}
      {renderSmartNotesSection()}
      {renderAIAssistantSection()}
    </MainSidebarContent>
  );
};

TranscriptionDisplay.propTypes = {
  transcript: PropTypes.arrayOf(
    PropTypes.shape({ time: PropTypes.string.isRequired, speaker: PropTypes.string.isRequired, text: PropTypes.string.isRequired })
  ).isRequired,
  isRecording: PropTypes.bool.isRequired,
  setIsRecording: PropTypes.func.isRequired,
  isPaused: PropTypes.bool.isRequired,
  setIsPaused: PropTypes.func.isRequired,
  onHighlightSummary: PropTypes.func.isRequired,
};

export default TranscriptionDisplay;